import {
  FunctionComponent,
  type CSSProperties,
  type ElementType,
  useState,
} from 'react';
import {
  HiPencil,
  HiEye,
  HiArrowUpTray,
  HiExclamationTriangle,
  HiCheckCircle,
} from 'react-icons/hi2';
import Pill from '../../../../ui/Pill';
import LoadingSpinner from '../../../../ui/LoadingSpinner';
import { Button, Tooltip } from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import DownloadIcon from '@mui/icons-material/Download';
import PublishIcon from '@mui/icons-material/Publish';
import { toast } from '../../../../toast/Toast';
import hotToast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { RaidrRoutePaths, RaidrV2RoutePaths } from '../../../../../routes/page-routes';
import LoadingButton from '../../../../ui/LoadingButton';
import { useMutationState } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { getKidOverviewHistoricalData } from '../../../../../api/kids/kids.routes';
import { getReviewVersionParams } from '../utils/reviewNavigation';
import { isShareClassBlockedByTemplateIncomplete } from '../utils/templateIncomplete.utils';
import { usePublishKidDocument } from '../../../../../api/kids/hooks/mutations';
import { KidDocumentIdentifierPayload } from '../../../../../api/kids/types';
import TokenService from '../../../../../services/token-service';
import GenericModal from '../../../../ui/GenericModal';
import { getStatusCounts } from '../overview/KidsOverviewV2Page';
import { KidDocumentStatus, KidFund, KID_STATUS_LABEL, KID_STATUS_TO_PILL } from '../types/KidEditorV2.types';
import { KID_STATUS_COLOR } from '../../../../../utilities/colorUtilities';
import { PillStatus } from '../../../../ui/Pill';

interface KidToolBarProps {
  funds: KidFund[];
  isLoading?: boolean;
  isError?: boolean;
}

type ToolbarPillConfig = {
  label: string;
  count: number;
  pillStatus: PillStatus;
  /** When set, the pill is a link (e.g. alerts → figures / template management). */
  href?: string;
  linkTitle?: string;
};

type KidToolbarProgressStage = {
  name: string;
  count: number;
  icon: ElementType<{ className?: string; style?: CSSProperties }>;
  statusColor: string;
  key: string;
  completed: boolean;
  items: unknown[];
  pills: ToolbarPillConfig[];
};

const KidToolBar: FunctionComponent<KidToolBarProps> = ({
  funds,
  isLoading = false,
  isError = false,
}) => {
  const statusCounts = getStatusCounts(funds);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user_client_id: clientId } = TokenService.getUser()!;
  const publishMutation = usePublishKidDocument();

  // Track all publish mutations using mutation state
  const allMutations = useMutationState({
    filters: {
      mutationKey: ['publish-kid-document'],
    },
  });

  // Helper to get mutation state for a specific item by matching variables
  // Returns the first matching mutation (React Query handles deduplication)
  const getItemMutationState = (item: {
    fundId: string;
    shareClassId: string;
    documentLanguage: string;
  }) => {
    return allMutations.find((mutation) => {
      const variables = mutation.variables as
        | KidDocumentIdentifierPayload
        | undefined;
      return (
        variables &&
        variables.fund_id === item.fundId &&
        variables.share_class_id === item.shareClassId &&
        variables.document_language === item.documentLanguage
      );
    });
  };

  // Calculate totals for progress display
  const totalEditing =
    statusCounts.editing.editing + statusCounts.editing.rejected;
  const totalReview = statusCounts.review.pendingReview;
  const totalPublished = statusCounts.publish.readyToPublish;

  // Build item lists per stage for dropdown details
  // "Editing" stage: documents currently being edited
  const editedItems = funds.flatMap((fund) =>
    fund.shareClasses
      .filter((sc) => sc.status === KidDocumentStatus.Editing)
      .map((sc) => ({
        fundName: fund.fund_name_full,
        fundId: fund.fund_id,
        isin: sc.share_class_isin,
        shareClassId: sc.share_class_id,
        version: sc.version_number,
      })),
  );

  // "Editing" stage: rejected documents
  const rejectedItems = funds.flatMap((fund) =>
    fund.shareClasses
      .filter((sc) => sc.status === KidDocumentStatus.Rejected)
      .map((sc) => ({
        fundName: fund.fund_name_full,
        fundId: fund.fund_id,
        isin: sc.share_class_isin,
        shareClassId: sc.share_class_id,
        version: sc.version_number,
      })),
  );

  // "Review" stage: documents that are edited / ready for review
  const reviewItems = funds.flatMap((fund) =>
    fund.shareClasses
      .filter(
        (sc) =>
          sc.status === KidDocumentStatus.Edited &&
          !isShareClassBlockedByTemplateIncomplete(sc),
      )
      .map((sc) => ({
        fundName: fund.fund_name,
        fundNameFull: fund.fund_name_full,
        shareClassNameLong: sc.share_class_name_full ?? sc.share_class_name ?? sc.share_class_isin,
        fundId: fund.fund_id,
        isin: sc.share_class_isin,
        shareClassId: sc.share_class_id,
        version: sc.version_number,
        documentLanguage: sc.document_language || 'English',
      })),
  );

  // "Publish" stage: documents that are reviewed / ready to publish
  const publishItems = funds.flatMap((fund) =>
    fund.shareClasses
      .filter(
        (sc) =>
          sc.status === KidDocumentStatus.Reviewed &&
          !isShareClassBlockedByTemplateIncomplete(sc),
      )
      .map((sc) => ({
        fundName: fund.fund_name_full,
        fundId: fund.fund_id,
        isin: sc.share_class_isin,
        shareClassId: sc.share_class_id,
        version: sc.version_number,
        documentLanguage: sc.document_language,
      })),
  );

  const alertItems = funds.flatMap((fund) =>
    fund.shareClasses
      .filter((sc) => sc.has_alert)
      .map((sc) => ({
        fundName: fund.fund_name_full,
        isin: sc.share_class_isin,
        version: sc.version_number,
      })),
  );

  const templateOutOfSyncItems = funds.flatMap((fund) =>
    fund.shareClasses
      .filter((sc) => sc.has_shareclass_out_of_sync)
      .map((sc) => ({
        fundName: fund.fund_name_full,
        fundId: fund.fund_id,
        isin: sc.share_class_isin,
        shareClassId: sc.share_class_id,
        documentLanguage: sc.document_language || 'English',
        version: sc.version_number,
      })),
  );

  const totalFiguresAlerts = statusCounts.alerts.figuresAlert;
  const totalTemplateSyncAlerts = statusCounts.alerts.shareClassOutOfSync;
  const totalAlerts = totalFiguresAlerts + totalTemplateSyncAlerts;

  const stages: KidToolbarProgressStage[] = [
    {
      name: 'Editing',
      count: totalEditing,
      icon: HiPencil,
      statusColor: KID_STATUS_COLOR[KidDocumentStatus.Editing],
      key: 'editing',
      completed: totalEditing > 0,
      items: [...editedItems, ...rejectedItems],
      pills: [
        {
          label: KID_STATUS_LABEL[KidDocumentStatus.Editing],
          count: statusCounts.editing.editing,
          pillStatus: KID_STATUS_TO_PILL[KidDocumentStatus.Editing] as PillStatus,
        },
        {
          label: KID_STATUS_LABEL[KidDocumentStatus.Rejected],
          count: statusCounts.editing.rejected,
          pillStatus: KID_STATUS_TO_PILL[KidDocumentStatus.Rejected] as PillStatus,
        },
      ] satisfies ToolbarPillConfig[],
    },
    {
      name: 'Review',
      count: totalReview,
      icon: HiEye,
      statusColor: KID_STATUS_COLOR[KidDocumentStatus.Edited],
      key: 'review',
      completed: totalReview > 0,
      items: reviewItems,
      pills: [
        {
          label: KID_STATUS_LABEL[KidDocumentStatus.Edited],
          count: statusCounts.review.pendingReview,
          pillStatus: KID_STATUS_TO_PILL[KidDocumentStatus.Edited] as PillStatus,
        },
      ] satisfies ToolbarPillConfig[],
    },
    {
      name: 'Publish',
      count: totalPublished,
      icon: HiArrowUpTray,
      statusColor: KID_STATUS_COLOR[KidDocumentStatus.Reviewed],
      key: 'publish',
      completed: totalPublished > 0,
      items: publishItems,
      pills: [
        {
          label: KID_STATUS_LABEL[KidDocumentStatus.Reviewed],
          count: statusCounts.publish.readyToPublish,
          pillStatus: KID_STATUS_TO_PILL[KidDocumentStatus.Reviewed] as PillStatus,
        },
      ] satisfies ToolbarPillConfig[],
    },
    {
      name: 'Alerts',
      count: totalAlerts,
      icon: HiExclamationTriangle,
      statusColor: KID_STATUS_COLOR[KidDocumentStatus.FiguresAlert],
      key: 'alerts',
      completed: totalAlerts > 0,
      items: [...alertItems, ...templateOutOfSyncItems],
      pills: [
        {
          label: KID_STATUS_LABEL[KidDocumentStatus.FiguresAlert],
          count: totalFiguresAlerts,
          pillStatus: KID_STATUS_TO_PILL[KidDocumentStatus.FiguresAlert] as PillStatus,
          href: RaidrRoutePaths.KIDS_FIGURES,
          linkTitle: 'Open KIDS Figures',
        },
        {
          label: 'Template incomplete',
          count: totalTemplateSyncAlerts,
          pillStatus: 'fail' as PillStatus,
          href: RaidrV2RoutePaths.TEMPLATE_MANAGEMENT,
          linkTitle: 'Open template management',
        },
      ] satisfies ToolbarPillConfig[],
    },
  ];

  return (
    <header
      className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm"
      role="toolbar"
      aria-label="KID document status and actions"
    >
      <div className="flex flex-col items-stretch justify-between gap-3 md:flex-row md:items-start md:gap-2">
        <section className="flex-1" aria-labelledby="kid-progress-heading">
          <header className="mb-2 flex w-full items-center gap-2 border-b border-gray-200">
            <h2
              id="kid-progress-heading"
              className="text-xs font-semibold uppercase tracking-wide text-mainblue sm:text-sm"
            >
              PRIIPs KID Status
            </h2>
          </header>

          <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-gray-200 bg-gray-200 sm:grid-cols-4">
            {stages.map((stage) => {
              const Icon = stage.icon;
              return (
                <div key={stage.name} className="flex flex-col items-stretch border-gray-200 bg-white [&:not(:last-child)]:border-b sm:[&:not(:last-child)]:border-b-0 sm:[&:not(:last-child)]:border-r">
                  <div className="flex flex-col items-center px-3 py-2">
                    <div
                      className="mb-1.5 flex h-10 w-10 items-center justify-center rounded-md"
                      style={{ color: stage.statusColor }}
                    >
                      <span className="text-lg font-bold leading-none">{stage.count}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon className="h-3 w-3" style={{ color: stage.statusColor }} />
                      <h3 className="text-xs font-semibold text-gray-700">{stage.name}</h3>
                    </div>
                  </div>

                  <div className="h-px w-full bg-gray-200" />

                  <div className="flex flex-wrap items-center justify-center gap-1.5 px-2 py-2">
                    {isLoading ? (
                      <LoadingSpinner className="h-3 w-3 border-2" />
                    ) : isError ? (
                      <div className="flex flex-col items-center gap-1">
                        <HiExclamationTriangle className="h-4 w-4 text-fail" />
                        <p className="text-xs font-medium text-fail">Failed</p>
                      </div>
                    ) : (
                      stage.pills
                        .filter((pill) => pill.count > 0)
                        .map((pill, pillIndex) => {
                          const labelText = `${pill.label} (${pill.count})`;
                          const pillEl = (
                            <Pill
                              status={pill.pillStatus}
                              className="text-center rounded-md text-[10px] sm:text-xs"
                            >
                              {labelText}
                            </Pill>
                          );
                          if (pill.href) {
                            return (
                              <Link
                                key={pillIndex}
                                to={pill.href}
                                title={pill.linkTitle}
                                className="inline-flex cursor-pointer rounded-md no-underline outline-none ring-offset-2 transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-mainblue"
                              >
                                {pillEl}
                              </Link>
                            );
                          }
                          return <span key={pillIndex}>{pillEl}</span>;
                        })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div
          className="mx-3 hidden self-stretch border-l border-gray-200 md:block"
          aria-hidden="true"
          role="separator"
        />

        <section className="flex flex-shrink-0 flex-col" aria-labelledby="batch-actions-heading">
          <header className="mb-2 flex items-center gap-2 border-b border-gray-200">
            <h2
              id="batch-actions-heading"
              className="text-xs font-semibold uppercase tracking-wide text-mainblue sm:text-sm"
            >
              Batch Actions
            </h2>
          </header>
          <nav className="flex flex-col items-stretch gap-1.5" aria-label="Batch action buttons">
            <Tooltip title={`Review all ${totalReview} documents ready for review`} placement="top" arrow>
              <span>
                <Button
                  onClick={() => setIsReviewModalOpen(true)}
                  disabled={totalReview === 0}
                  variant="outlined"
                  size="small"
                  className="w-full normal-case disabled:!opacity-40"
                  style={{ borderColor: KID_STATUS_COLOR[KidDocumentStatus.Edited], color: KID_STATUS_COLOR[KidDocumentStatus.Edited] }}
                  startIcon={<RateReviewIcon style={{ fontSize: 14 }} />}
                >
                  <span className="text-xs font-semibold">Review All ({totalReview})</span>
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={`Publish all ${totalPublished} documents ready to publish`} placement="top" arrow>
              <span>
                <Button
                  onClick={() => setIsPublishModalOpen(true)}
                  disabled={totalPublished === 0}
                  variant="outlined"
                  size="small"
                  className="w-full normal-case disabled:!opacity-40"
                  style={{ borderColor: KID_STATUS_COLOR[KidDocumentStatus.Reviewed], color: KID_STATUS_COLOR[KidDocumentStatus.Reviewed] }}
                  startIcon={<PublishIcon style={{ fontSize: 14 }} />}
                >
                  <span className="text-xs font-semibold">Publish All ({totalPublished})</span>
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Download all latest published documents" placement="top" arrow>
              <span>
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  className="w-full normal-case !text-white bg-mainblue hover:bg-mainblue/90"
                  startIcon={<DownloadIcon style={{ fontSize: 14 }} />}
                >
                  <span className="text-xs font-semibold">Download All</span>
                </Button>
              </span>
            </Tooltip>
          </nav>
        </section>
      </div>

      {/* Review All Modal */}
      <GenericModal
        isOpen={isReviewModalOpen}
        setIsOpen={setIsReviewModalOpen}
        title="Review All Documents"
        className="max-w-2xl"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            {reviewItems.length > 0
              ? `The following ${reviewItems.length} KID document${reviewItems.length === 1 ? '' : 's'} ${reviewItems.length === 1 ? 'is' : 'are'} ready for review:`
              : 'No items ready for review.'}
          </p>

          {reviewItems.length > 0 ? (
            <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Fund ID
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Fund Name
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Share Class Name
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      ISIN
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {reviewItems.map((item, idx) => (
                    <tr key={idx} className="transition-colors hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.fundName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.fundNameFull}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.shareClassNameLong}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.isin}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Button
                          variant="contained"
                          disableElevation
                          size="small"
                          className="normal-case !text-white hover:brightness-90 disabled:!opacity-40"
                          onClick={async () => {
                            const historicalData = await queryClient.fetchQuery({
                              queryKey: [
                                'kid-historical-overview',
                                clientId,
                                item.fundId,
                                item.shareClassId,
                                item.documentLanguage,
                              ],
                              queryFn: () =>
                                getKidOverviewHistoricalData(
                                  clientId,
                                  item.fundId,
                                  item.shareClassId,
                                  item.documentLanguage,
                                ),
                            });
                            const params = getReviewVersionParams(historicalData);
                            setIsReviewModalOpen(false);
                            const search = new URLSearchParams({
                              fundId: item.fundId,
                              shareClassId: item.shareClassId,
                              documentLanguage: item.documentLanguage,
                            });
                            if (params) {
                              search.set('targetVersion', params.targetVersion);
                              search.set('compareVersion', params.compareVersion);
                            }
                            navigate(`${RaidrV2RoutePaths.KID_REVIEW_V2}?${search.toString()}`, {
                              state: { reviewItems, currentIndex: idx },
                            });
                          }}

                        >
                          Review
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-4 text-center text-gray-500">
              No items ready for review
            </p>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="contained"
              disableElevation
              size="small"
              onClick={() => setIsReviewModalOpen(false)}
              className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300"
            >
              Cancel
            </Button>
            {reviewItems.length > 0 && (
              <Button
                variant="contained"
                disableElevation
                size="small"
                onClick={async () => {
                  const firstItem = reviewItems[0];
                  const historicalData = await queryClient.fetchQuery({
                    queryKey: [
                      'kid-historical-overview',
                      clientId,
                      firstItem.fundId,
                      firstItem.shareClassId,
                      firstItem.documentLanguage,
                    ],
                    queryFn: () =>
                      getKidOverviewHistoricalData(
                        clientId,
                        firstItem.fundId,
                        firstItem.shareClassId,
                        firstItem.documentLanguage,
                      ),
                  });
                  const params = getReviewVersionParams(historicalData);
                  setIsReviewModalOpen(false);
                  const search = new URLSearchParams({
                    fundId: firstItem.fundId,
                    shareClassId: firstItem.shareClassId,
                    documentLanguage: firstItem.documentLanguage,
                  });
                  if (params) {
                    search.set('targetVersion', params.targetVersion);
                    search.set('compareVersion', params.compareVersion);
                  }
                  navigate(`${RaidrV2RoutePaths.KID_REVIEW_V2}?${search.toString()}`, {
                    state: { reviewItems, currentIndex: 0 },
                  });
                }}
                className="normal-case !text-white hover:brightness-90 disabled:!opacity-40"
                style={{ backgroundColor: KID_STATUS_COLOR[KidDocumentStatus.Edited] }}
              >
                Start Review ({totalReview})
              </Button>
            )}
          </div>
        </div>
      </GenericModal>

      {/* Publish All Modal */}
      {(() => {
        // Calculate overall state from mutations
        const publishItemMutations = publishItems.map((item) =>
          getItemMutationState({
            fundId: item.fundId,
            shareClassId: item.shareClassId,
            documentLanguage: item.documentLanguage,
          }),
        );
        const pendingMutations = publishItemMutations.filter(
          (m) => m?.status === 'pending',
        );
        const successMutations = publishItemMutations.filter(
          (m) => m?.status === 'success',
        );
        const errorMutations = publishItemMutations.filter(
          (m) => m?.status === 'error',
        );
        const isPublishing = pendingMutations.length > 0;
        const allCompleted =
          successMutations.length + errorMutations.length ===
          publishItems.length;
        const hasErrors = errorMutations.length > 0;
        const remainingCount =
          publishItems.length - successMutations.length - errorMutations.length;

        return (
          <GenericModal
            isOpen={isPublishModalOpen}
            setIsOpen={(open) => {
              setIsPublishModalOpen(open);
              if (!open) {
                publishMutation.reset();
              }
            }}
            title="Publish All Documents"
            className="max-w-2xl"
          >
            <div className="space-y-4">
              <p className="text-gray-700">
                {publishItems.length > 0
                  ? `The following ${publishItems.length} KID document${publishItems.length === 1 ? '' : 's'} ${publishItems.length === 1 ? 'is' : 'are'} ready to publish:`
                  : 'No items ready to publish.'}
              </p>

              {publishItems.length > 0 ? (
                <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                          Fund Name
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                          ISIN
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                          Version
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {publishItems.map((item, idx) => {
                        const mutationState = getItemMutationState({
                          fundId: item.fundId,
                          shareClassId: item.shareClassId,
                          documentLanguage: item.documentLanguage,
                        });
                        const isPending = mutationState?.status === 'pending';
                        const isSuccess = mutationState?.status === 'success';
                        const isError = mutationState?.status === 'error';

                        return (
                          <tr
                            key={idx}
                            className={`transition-colors ${isPending
                              ? 'border-l-4 border-blue-500 bg-blue-50'
                              : isSuccess
                                ? 'border-l-4 border-green-500 bg-green-50'
                                : isError
                                  ? 'border-l-4 border-red-500 bg-red-50'
                                  : ''
                              }`}
                          >
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {item.fundName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {item.isin}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div className="flex items-center gap-2">
                                {item.version}
                                {isPending && (
                                  <LoadingSpinner className="h-4 w-4 flex-shrink-0 border-2" />
                                )}
                                {isSuccess && (
                                  <HiCheckCircle className="h-4 w-4 flex-shrink-0 text-green-500" />
                                )}
                                {isError && (
                                  <HiExclamationTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-4 text-center text-gray-500">
                  No items ready to publish
                </p>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsPublishModalOpen(false);
                    publishMutation.reset();
                  }}
                  disabled={isPublishing}
                  className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                {publishItems.length > 0 && (
                  <LoadingButton
                    type="button"
                    onClick={async () => {
                      // Transform items to API payload format
                      const itemsToPublish: KidDocumentIdentifierPayload[] =
                        publishItems.map((item) => ({
                          client_id: clientId,
                          fund_id: item.fundId,
                          share_class_id: item.shareClassId,
                          document_language: item.documentLanguage,
                        }));

                      // Show loading toast
                      const loadingToastId = toast({
                        title: 'Publishing',
                        message: `Publishing ${itemsToPublish.length} KID document(s)...`,
                        type: 'loading',
                        duration: Infinity,
                      });

                      // Publish items one by one
                      const results: Array<{
                        item: KidDocumentIdentifierPayload;
                        success: boolean;
                        error?: Error;
                      }> = [];

                      for (let i = 0; i < itemsToPublish.length; i++) {
                        const item = itemsToPublish[i];
                        const itemMutation = getItemMutationState({
                          fundId: item.fund_id,
                          shareClassId: item.share_class_id,
                          documentLanguage: item.document_language,
                        });

                        // Skip if already completed (success or error)
                        if (
                          itemMutation?.status === 'success' ||
                          itemMutation?.status === 'error'
                        ) {
                          // Track existing results
                          const existingResult =
                            itemMutation?.status === 'success';
                          results.push({
                            item,
                            success: existingResult,
                            error: existingResult
                              ? undefined
                              : (itemMutation?.error as Error),
                          });
                          continue;
                        }

                        try {
                          // Use mutation - state is tracked by variables via mutationKey
                          await publishMutation.mutateAsync(item);
                          results.push({ item, success: true });
                        } catch (error) {
                          results.push({
                            item,
                            success: false,
                            error: error as Error,
                          });
                        }
                      }

                      // Close loading toast
                      hotToast.remove(loadingToastId);

                      // Show summary toast
                      const successCount = results.filter(
                        (r) => r.success,
                      ).length;
                      const failCount = results.filter(
                        (r) => !r.success,
                      ).length;
                      const totalCount = itemsToPublish.length;

                      if (failCount === 0) {
                        toast({
                          title: 'Success',
                          message: `All ${totalCount} KID document(s) published successfully`,
                          type: 'success',
                        });
                      } else if (successCount > 0) {
                        toast({
                          title: 'Partial Success',
                          message: `${successCount} of ${totalCount} KID document(s) published. ${failCount} failed.`,
                          type: 'warning',
                        });
                      } else {
                        toast({
                          title: 'Error',
                          message: `Failed to publish all ${totalCount} KID document(s)`,
                          type: 'error',
                        });
                      }
                    }}
                    disabled={isPublishing}
                    isSuccess={
                      allCompleted && !hasErrors && publishItems.length > 0
                    }
                    isError={hasErrors}
                    isLoading={isPublishing}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    Publish All ({totalPublished})
                  </LoadingButton>
                )}
              </div>
            </div>
          </GenericModal>
        );
      })()}
    </header>
  );
};

export default KidToolBar;